/**
 * Fixed-capacity ring buffer of game events stored in typed arrays.
 * Systems push during the tick; presentation layers drain after the tick.
 * No objects are created per event.
 */
export enum GameEvent {
  None = 0,
  EnemyDied,
  SpawnerHit,
  SpawnerDestroyed,
  PlayerHit,
  PlayerDied,
  PickupWeapon,
  Overcharge,
  WeaponExpired,
  PickupGem,
  PickupPrize,
  RoomEnter,
  RoomCleared,
  RoomNext,
  StallWarning,
  DragonSpawn,
  /** Dragon fire landed on a player. */
  DragonBurn,
  /** Dragon winds up a breath (a = eid). */
  DragonRoar,
  Shot,
  Shockwave,
  PlayerIdle,
  /** Kill chain milestone (a = combo count). */
  KillStreak,
  /** Player dropped under 20 percent health. */
  NearDeath,
  /** Every room on the floor grid cleared and the exit taken (a = floor index). */
  FloorCleared,
  /** Final-floor archmage is down; the run is won (a = floor, b = score). */
  RunWon,
  /** Prize wave started after a clear. */
  PrizeWave,
  /** Gem grab streak (a = gems in window). */
  GemGreed,
  /** Rune heart grabbed (a = player, b = hp restored). */
  PickupHeart,
  /** An Epic Weapon (b = weapon id) or heart (b = -1) drifted in through a gate mid-fight (a = gate side). */
  SupplyDrop,
  /** First time this run a new small-fry guest is the room's featured pack (a = EnemyKind). */
  NewThreat,
  /** Floor boss entered the exit room (a = eid, b = BossKind). */
  BossSpawn,
  /** Boss telegraphs a spell (a = eid, b = pattern id). */
  BossTelegraph,
  /** Boss releases a spell volley (a = eid, b = pattern id). */
  BossCast,
  /** Boss took a hit (a = eid, b = rounded damage). */
  BossHit,
  /** Boss dropped under half health (a = eid, b = BossKind). */
  BossHalf,
  /** Boss blinked to a new rune point (a = eid). */
  BossBlink,
  /** Boss died; the death crescendo starts (a = eid, b = BossKind). */
  BossDeath,
  /** Last portal fell in a fight room: the rune circle ignites and a boss is on its way (a = BossKind, b = room index). */
  BossIncoming,
  /** Boss landed / rose / blinked in and is now vulnerable (a = eid, b = BossKind). */
  BossLanded,
  /** Boss stomped, charged or dove: heavy impact at x,y (a = BossImpactKind, b = BossKind). */
  BossImpact,
  /** Boss split off or summoned minions (a = eid, b = count). */
  BossSummon,
  /** Boss death crescendo finished and the loot fountain is out (a = eid, b = BossKind). */
  BossDown,
  /** Regular enemy took a hit and lived (a = WeaponId, b = EnemyKind). */
  EnemyHit,
  /** A cinder skull went off (a = eid). */
  CinderBlast,
  /** A dormant mimic woke up (a = eid). */
  MimicWake,
  /** A mimic was put down and paid out (a = eid). */
  MimicSlain,
  /** A brute charge ended against the arena wall (a = eid). */
  BruteWall,
  /** New floor's rule announced (a = FloorModifier, b = floor index). */
  FloorModifier,
  /** Champion's Ring challenge started (a = BossKind). */
  ArenaStart,
  /** Sanctum reached: a quiet room with a free heal (a = player eid). */
  SanctumRest,
  /** A floor mine's fuse was lit (a = eid). */
  MineArmed,
  /** A floor mine detonated (a = eid). */
  MineBlast,
  /** Cataclysm Orb fired: room-wide enemy damage (a = player eid). */
  Cataclysm,
  /** Off-hand Rune Ward grabbed (a = player). */
  PickupShield,
  /** Gale Step grabbed (a = player). */
  PickupSwift,
  /** Ward timer ran out (a = player). */
  OffhandExpired,
  /** Gale Step timer ran out (a = player). */
  SwiftExpired,
  /** Ward blade hit something (a = owner, b = target). */
  OffhandHit,
  /** Player double-tap dash (a = player). */
  PlayerDash,
}

/** Flavour of a `BossImpact` so audio can pick the right hit. */
export enum BossImpactKind {
  /** Crash-down or swoop landing at the end of an entrance. */
  Land = 0,
  /** Ground stomp: shockwave ring plus rubble. */
  Stomp = 1,
  /** Charge or dive ended against the arena wall. */
  Wall = 2,
  /** Screech pulse. */
  Screech = 3,
  /** Acid glob burst on the floor. */
  Splat = 4,
  /** Colossus split off a chunk of itself. */
  Split = 5,
}

export interface EventView {
  readonly type: GameEvent;
  /** Primary payload (entity id, weapon id, etc). */
  readonly a: number;
  /** Secondary payload. */
  readonly b: number;
  readonly x: number;
  readonly y: number;
}

export class EventQueue {
  private readonly type: Uint8Array;
  private readonly a: Int32Array;
  private readonly b: Int32Array;
  private readonly x: Float32Array;
  private readonly y: Float32Array;
  private head = 0;
  private tail = 0;
  private readonly capacity: number;
  private readonly view: { type: GameEvent; a: number; b: number; x: number; y: number } = {
    type: GameEvent.None,
    a: 0,
    b: 0,
    x: 0,
    y: 0,
  };

  constructor(capacity = 1024) {
    this.capacity = capacity;
    this.type = new Uint8Array(capacity);
    this.a = new Int32Array(capacity);
    this.b = new Int32Array(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
  }

  push(type: GameEvent, a = 0, b = 0, x = 0, y = 0): void {
    const next = (this.tail + 1) % this.capacity;
    if (next === this.head) return; // full: drop newest, keep the sim alive
    this.type[this.tail] = type;
    this.a[this.tail] = a;
    this.b[this.tail] = b;
    this.x[this.tail] = x;
    this.y[this.tail] = y;
    this.tail = next;
  }

  get length(): number {
    return (this.tail - this.head + this.capacity) % this.capacity;
  }

  /** Iterate without consuming. Returns a reused view object. */
  peekAt(i: number): EventView {
    const idx = (this.head + i) % this.capacity;
    this.view.type = this.type[idx] as GameEvent;
    this.view.a = this.a[idx];
    this.view.b = this.b[idx];
    this.view.x = this.x[idx];
    this.view.y = this.y[idx];
    return this.view;
  }

  clear(): void {
    this.head = this.tail;
  }
}
